import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { MultiFactorError } from 'firebase/auth';
import { Form, Formik } from 'formik';

import { Input } from '@/common';
import { SsoProviderButtons } from '@/components/auth/SsoProviderButtons';
import { Button } from '@/components/generic';
import {
  hasLoginSecondaryProviders,
  isLoginPasswordPrimary,
  isPasswordProvider,
  loginPrimarySsoProvider,
  loginSecondaryProviders
} from '@/configuration/ssoProviders';
import { AuthContext } from '@/contexts';
import { PUBLIC_PAGE_ROUTES } from '@/enums';
import {
  AUTH_ERROR_MESSAGE_KEYS,
  handleFirebaseAuthError,
  handleSsoSignInError,
  isAccountExistsWithDifferentCredentialError,
  isMissingAppUserError,
  isSsoPopupCancelledError
} from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { PasswordInput } from '@/pages/Login/PasswordInput';
import { createLoginValidationSchema } from '@/pages/Login/Validation';
import { SentoaLogo } from '@/resources';
import { TestIds } from '@/shared/test-ids';
import { LoginCredentials } from '@/types';
import { SsoProviderConfig } from '@/types/ssoProvider';

export const LoginForm = () => {
  const navigate = useNavigate();
  const [t, i18n] = useTranslation('common');
  const { login, loginWithSsoProvider, pendingSsoLinkEmail, cancelPendingSsoLink } =
    useContext(AuthContext);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [submittingProviderId, setSubmittingProviderId] = useState<string | null>(null);
  const isSsoMerge = !!pendingSsoLinkEmail;
  const showPrimaryPassword = isLoginPasswordPrimary || isSsoMerge;
  const showSecondarySection = !isSsoMerge && hasLoginSecondaryProviders;
  const secondarySsoProviders = loginSecondaryProviders.filter(
    (provider): provider is SsoProviderConfig => !isPasswordProvider(provider)
  );

  const initialValues: LoginCredentials = {
    email: pendingSsoLinkEmail ?? '',
    password: ''
  };
  const validationSchema = useMemo(
    () => createLoginValidationSchema(isSsoMerge),
    [i18n.language, isSsoMerge]
  );

  const handleSubmit = async (values: LoginCredentials) => {
    const email = isSsoMerge ? pendingSsoLinkEmail : values.email;
    try {
      await login(email, values.password);
    } catch (res) {
      const error = res as { code: string };
      if (error.code === 'auth/multi-factor-auth-required') {
        navigate(PUBLIC_PAGE_ROUTES.MFA_VERIFY, {
          replace: true,
          state: { mfaError: res as MultiFactorError }
        });
        return;
      }
      showToast(ToastType.ERROR, t(handleFirebaseAuthError(error.code)));
    }
  };

  const handleSsoLogin = async (providerId: string) => {
    setSubmittingProviderId(providerId);
    try {
      await loginWithSsoProvider(providerId);
    } catch (error) {
      if (isSsoPopupCancelledError(error)) {
        return;
      }
      if (isAccountExistsWithDifferentCredentialError(error)) {
        return;
      }
      if (isMissingAppUserError(error)) {
        showToast(ToastType.ERROR, t(AUTH_ERROR_MESSAGE_KEYS.NO_APP_ACCOUNT_FOR_SSO_USER));
        return;
      }
      showToast(ToastType.ERROR, t(handleSsoSignInError(error)));
    } finally {
      setSubmittingProviderId(null);
    }
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const passwordForm = (options: { includeSsoMergeActions: boolean }) => (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={validationSchema}>
      {({ errors, touched, values, setFieldTouched, setFieldValue }) => (
        <Form className='flex flex-col gap-y-4'>
          {isSsoMerge ? (
            <p
              data-testid={TestIds.login.ssoLinkHint}
              className='text-center text-sm text-primary-black'>
              {t('ssoAccountMergeDescription', { email: pendingSsoLinkEmail })}
            </p>
          ) : (
            <Input
              id='email'
              type='text'
              dataTestId={TestIds.login.emailInput}
              label={t('username')}
              placeholder={t('email')}
              value={values.email}
              handleChange={(value) => {
                setFieldValue('email', value.trimStart());
              }}
              onBlur={() => setFieldTouched('email', true)}
              errorMessage={touched.email ? errors.email : undefined}
              isInvalid={!!errors.email && touched.email}
            />
          )}
          <PasswordInput
            type={showPassword ? 'text' : 'password'}
            id='password'
            inputTestId={TestIds.login.passwordInput}
            toggleTestId={TestIds.login.passwordToggle}
            label={t('password')}
            showPassword={handleShowPassword}
            errorMessage={touched.password ? errors.password : undefined}
            isInvalid={!!errors.password && touched.password}
            passwordValue={values.password}
          />
          {!isSsoMerge && (
            <div className='flex justify-end'>
              <Link
                data-testid={TestIds.login.forgotPasswordLink}
                to={'/sendEmail'}
                className='text-sm text-primary-blue hover:underline'>
                {t('forgotYourPassword')}
              </Link>
            </div>
          )}
          <Button dataTestId={TestIds.login.submitButton} type='submit' className='w-full'>
            {isSsoMerge ? t('ssoAccountMergeConfirm') : t('login')}
          </Button>
          {options.includeSsoMergeActions && isSsoMerge && (
            <button
              type='button'
              className='text-sm text-primary-blue hover:underline'
              onClick={cancelPendingSsoLink}>
              {t('cancel')}
            </button>
          )}
        </Form>
      )}
    </Formik>
  );

  return (
    <div data-testid={TestIds.login.form} className='flex w-full flex-col gap-6'>
      <div data-testid={TestIds.login.logo} className='flex w-full justify-center'>
        <SentoaLogo className='h-10 w-44' />
      </div>
      <div className='flex justify-center'>
        <div className='h-px w-full bg-blue-powder' />
      </div>

      <div className='flex flex-col gap-4'>
        {showPrimaryPassword && passwordForm({ includeSsoMergeActions: true })}

        {!isSsoMerge && loginPrimarySsoProvider && (
          <SsoProviderButtons
            providers={[loginPrimarySsoProvider]}
            onProviderClick={handleSsoLogin}
            submittingProviderId={submittingProviderId}
            showDivider={false}
            showLabels
            buttonTestIdPrefix={TestIds.login.ssoButton}
          />
        )}

        {showSecondarySection && (
          <div data-testid={TestIds.login.secondaryAuthSection} className='flex flex-col gap-4'>
            <div className='flex items-center gap-3'>
              <div className='h-px flex-1 bg-blue-powder' />
              <span className='text-sm text-primary-black'>{t('or')}</span>
              <div className='h-px flex-1 bg-blue-powder' />
            </div>
            {loginSecondaryProviders.map((provider) => {
              if (isPasswordProvider(provider)) {
                return (
                  <div key={provider.id}>{passwordForm({ includeSsoMergeActions: false })}</div>
                );
              }
              if (provider.id !== secondarySsoProviders[0]?.id) {
                return null;
              }
              return (
                <SsoProviderButtons
                  key='secondary-sso'
                  providers={secondarySsoProviders}
                  onProviderClick={handleSsoLogin}
                  submittingProviderId={submittingProviderId}
                  showDivider={false}
                  showLabels={false}
                  buttonTestIdPrefix={TestIds.login.ssoButton}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
